import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ApiBaseService } from './api-base.service';
import { AppGlobals } from '../app.globals';
import { FeatureFlags } from '../interfaces/enums/feature-flag';

export interface SocietyContactRef {
  contactId: number | null;
  /** 'society' only for shareholders: a Gesellschaft holding shares. */
  contactType: 'company' | 'person' | 'society' | null;
}

export type ShareholderRole = 'general_partner' | 'limited_partner';

/** Geschäftsführung, Prokurist, Vorstand. */
export type RepresentativeFunction = 'managing_director' | 'authorized_officer' | 'board_member';

export interface SocietyRepresentativeItem extends SocietyContactRef {
  function: RepresentativeFunction | null;
}

export interface SocietyShareholderItem extends SocietyContactRef {
  /** Komplementär / Kommanditist; null for a plain shareholder. */
  role: ShareholderRole | null;
  share: number | null;
  /** Hafteinlage of a Kommanditist (KG forms only). */
  liabilityAmount?: number | null;
}

/** Verkaufsmodell, kept by the admins (Admin ▸ Gesellschaften). */
export interface SocietySalesModel {
  id: number;
  name: string;
}

/** A file already uploaded to a Gesellschaft. */
export interface SocietyDocumentItem {
  id: number;
  kind: SocietyDocumentKind;
  /** Blob name (GUID + extension), opened with the file viewer type "society-file". */
  uuid: string;
  name: string;
}

export type SocietyDocumentKind = 'ubo' | 'accounting' | 'founding_deed' | 'shareholder_list'
  | 'register_extract' | 'transparency' | 'account_opening' | 'payment_receipt' | 'bank_confirmation'
  | 'bank_power' | 'registration_notice' | 'gez_registration' | 'gez_exemption';

/** The whole "Gesellschaft erstellen" form, as the API stores it. */
export interface SocietyItem {
  id: number;
  legalName: string;
  legalForm: string | null;
  status: string | null;
  transactionType: string | null;
  /** yyyy-MM-dd, so the day doesn't shift with the time zone. */
  foundingDate: string | null;
  /** Satzungssitz: street, zipcode, city (registeredSeat) and country. */
  registeredSeatAddress: string | null;
  registeredSeatZipcode: string | null;
  registeredSeat: string | null;
  registeredSeatCountryId: number | null;
  /** Unticked: the Geschäftsanschrift (address … countryId) is the Satzungssitz. */
  businessAddressDiffers: boolean;
  address: string | null;
  zipcode: string | null;
  city: string | null;
  countryId: number | null;
  registerCourt: string | null;
  registerNumber: string | null;
  shareCapital: number | null;
  /** Gesellschaftszweck. */
  purpose: string | null;
  representationRule: string | null;
  vatLiable: boolean;
  /** Steuernummer. */
  taxNumber: string | null;
  /** Umsatzsteuer-ID. */
  vatId: string | null;
  /** Steuerliche Betreuung. */
  taxCare: 'internal' | 'external' | null;
  salesModelId: number | null;
  taxAdvisor: SocietyContactRef | null;
  previousTaxAdvisor: SocietyContactRef | null;
  /** Zuständiger Buchhalter and its Vertretung; both optional. */
  accountant?: SocietyContactRef | null;
  accountantDeputy?: SocietyContactRef | null;
  articlesContractId: number | null;
  shareholders: SocietyShareholderItem[];
  /** Vertretung: Geschäftsführung, Prokuristen, Vorstand. */
  managingDirectors: SocietyRepresentativeItem[];
  projectIds: number[];
  contractIds: number[];
  documents?: SocietyDocumentItem[];
}

export interface SocietyListItem {
  id: number;
  legalName: string;
  legalForm: string | null;
  status: string | null;
  city: string | null;
  projectCount: number;
  /** Shaped like a contract's preview links (recordSource "project"), for the same pills. */
  projects: SocietyProjectLink[];
}

export interface SocietyProjectLink {
  recordSource: 'project';
  siteId: number | null;
  siteNr: string | null;
  projectId: number;
  projectNr: string | null;
  projectName: string | null;
}

/** A box of the org chart: a Gesellschaft, or a company/person holding shares. */
export interface SocietyOrgChartNode {
  /** "{type}:{id}", e.g. "society:5". */
  key: string;
  type: 'society' | 'company' | 'person';
  id: number;
  name: string;
  /** Gesellschaften: umsatzsteuerpflichtig (the green line on the whiteboard). */
  vatLiable?: boolean;
  /** Gesellschaften: their Verkaufsmodell, the band they're grouped in. */
  salesModelId?: number | null;
  /** Gesellschaften: names of the Geschäftsführung. */
  managingDirectors?: string[];
  /** Gesellschaften: zuständiger Buchhalter and its Vertretung. */
  accountant?: string | null;
  accountantDeputy?: string | null;
}

/** A line of the org chart: `from` holds `share` percent of `to`. */
export interface SocietyOrgChartEdge {
  from: string;
  to: string;
  /** Komplementär: solid line, Kommanditist: dashed. */
  role?: ShareholderRole | null;
  share: number | null;
}

/** A Gesellschaft as the contacts dropdown lists it (contactType 'society'). */
export interface SocietyContact {
  id: number;
  displayName: string;
}

/** Gesellschaften (SPVs) under Kontakte ▸ Gesellschaften. */
@Injectable({
  providedIn: 'root',
})
export class SocietyService {
  constructor(private apiService: ApiBaseService) {}

  /** Gesellschaften as contacts ({ id, displayName }) for every contacts dropdown; null until loaded. */
  readonly contacts$ = new BehaviorSubject<SocietyContact[] | null>(null);
  private contactsRequest: Promise<SocietyContact[]> | null = null;

  /** Loads them once per session; `force` after one was created (e.g. in another tab). */
  loadContacts(force = false): Promise<SocietyContact[]> {
    // Feature off: no call at all. The gated endpoint would answer 404 and open the error dialog
    // in every screen with a contacts dropdown.
    if (!AppGlobals.UserCan(FeatureFlags.Gesellschaften)) {
      return Promise.resolve([]);
    }
    if (!this.contactsRequest || force) {
      const request: Promise<SocietyContact[]> = this.getSocieties()
        .then(result => {
          if (!result || result.hasError) throw result?.feedback;
          const contacts = (result.societies ?? []).map(s => ({ id: s.id, displayName: s.legalName }));
          this.contacts$.next(contacts);
          return contacts;
        })
        // Not cached: the next caller asks again instead of every dropdown staying empty.
        .catch(() => {
          if (this.contactsRequest === request) this.contactsRequest = null;
          return this.contacts$.value ?? [];
        });
      this.contactsRequest = request;
    }
    return this.contactsRequest;
  }

  async getSocieties(): Promise<{ hasError?: boolean; feedback?: string; societies?: SocietyListItem[] }> {
    return this.apiService.httpRequest('SocietiesService/GetSocieties', {}, true, true);
  }

  async getOrgChart(): Promise<{
    hasError?: boolean;
    feedback?: string;
    nodes?: SocietyOrgChartNode[];
    edges?: SocietyOrgChartEdge[];
    salesModels?: SocietySalesModel[];
  }> {
    return this.apiService.httpRequest('SocietiesService/GetSocietyOrgChart', {}, true, true);
  }

  async getSalesModels(): Promise<{ hasError?: boolean; feedback?: string; salesModels?: SocietySalesModel[] }> {
    return this.apiService.httpRequest('SocietiesService/GetSalesModels', {}, true, true);
  }

  async saveSalesModel(salesModel: SocietySalesModel): Promise<{ hasError?: boolean; feedback?: string }> {
    return this.apiService.httpRequest('SocietiesService/SaveSalesModel', { SalesModel: salesModel }, true, true);
  }

  async deleteSalesModel(id: number): Promise<{ hasError?: boolean; feedback?: string }> {
    return this.apiService.httpRequest('SocietiesService/DeleteSalesModel', { SalesModel: { id } }, true, true);
  }

  async getSociety(societyId: number): Promise<{ hasError?: boolean; feedback?: string; society?: SocietyItem }> {
    return this.apiService.httpRequest('SocietiesService/GetSociety', { SocietyId: societyId }, true, true);
  }

  async saveSociety(
    society: SocietyItem,
    removedDocumentIds: number[]
  ): Promise<{ hasError?: boolean; feedback?: string; societyId?: number }> {
    return this.apiService.httpRequest(
      'SocietiesService/SaveSociety',
      { Society: society, RemovedDocumentIds: removedDocumentIds },
      true,
      true
    );
  }

  async uploadSocietyFile(societyId: number, kind: SocietyDocumentKind, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('societyId', String(societyId));
    formData.append('kind', kind);
    formData.append('documentName', file.name);
    formData.append('fileBytes', file);
    return this.apiService.httpRequestForm('SocietiesService/UploadSocietyFile', formData, true, true);
  }
}
